import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import OffensivePlayVolumeChart from '../TeamMetrics/OffensivePlayVolumeChart/OffensivePlayVolumeChart.js';

const TeamMatchupsTable = ({ matchups, team, metrics }) => {
	const [
		selectedResults,
		setSelectedResults
	] = useState(null);

	const formatDate = (dateString) => {
		if (!dateString) return 'TBD';
		// Date-only strings (e.g. "2026-09-13") are parsed as UTC midnight by
		// `new Date()`, which shifts the displayed day west of UTC. Parse the
		// parts manually so the date is interpreted in the user's local time.
		const [
			yearStr,
			monthStr,
			dayStr
		] = dateString.split('-');
		const date = new Date(
			Number(yearStr),
			Number(monthStr) - 1,
			Number(dayStr)
		);
		if (Number.isNaN(date.getTime())) return 'TBD';
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const leagueData =
		metrics && metrics.league_avg_recent_team_stats
			? metrics.league_avg_recent_team_stats
			: {};

	if (!matchups || matchups.length === 0) {
		return (
			<div className="mt-6">
				<div className="p-8 bg-white dark:bg-dark-card rounded-xl border border-gray-300 dark:border-dark-border shadow-lg text-gray-600 dark:text-gray-300 text-center">
					No matchups available for this season yet.
				</div>
			</div>
		);
	}

	return (
		<div className="mt-6">
			<div className="p-8 bg-white dark:bg-dark-card rounded-xl border border-gray-300 dark:border-dark-border shadow-lg text-gray-800 dark:text-dark-text">
				<div className="overflow-x-auto rounded-md border border-gray-300 dark:border-dark-border">
					<table className="min-w-full bg-white dark:bg-dark-card">
						<thead className="bg-gray-100 dark:bg-dark-bg text-black dark:text-dark-text p-4">
							<tr>
								<th className="px-8 py-4 text-left">Week</th>
								<th className="px-8 py-4 text-left">Opponent</th>
								<th className="px-8 py-4 text-left">Game Date</th>
								<th className="px-8 py-4 text-left">Location</th>
								<th className="px-8 py-4 text-left">Results</th>
							</tr>
						</thead>
						<tbody>
							{matchups.map((match) => (
								<tr key={match.id} className="border-t border-gray-300 dark:border-dark-border">
									<td className="px-8 py-4 text-gray-800 dark:text-dark-text">{match.week}</td>
									<td className="px-8 py-4 text-gray-800 dark:text-dark-text">
										<Link
											to={`/teams/${match.opponent_id}`}
											className="text-blue-500 hover:text-blue-600 hover:underline"
										>
											{match.opponent_name}
										</Link>
									</td>
									<td className="px-8 py-4 text-gray-800 dark:text-dark-text">
										{formatDate(match.game_date)}
									</td>
									<td className="px-8 py-4 text-gray-800 dark:text-dark-text">
										{match.is_home_game ? 'Home' : 'Away'}
									</td>
									<td className="px-8 py-4 text-gray-800 dark:text-dark-text">
										{match.results ? (
											<button
												onClick={() => setSelectedResults(match.results)}
												className="text-blue-500 hover:text-blue-600 hover:underline"
											>
												See results
											</button>
										) : (
											'-'
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{selectedResults && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text p-6 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
						<div className="flex justify-between items-center mb-6">
							<h2 className="text-2xl font-bold text-gray-800 dark:text-dark-text">Game Results</h2>
							<button
								onClick={() => setSelectedResults(null)}
								className="text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-dark-text text-2xl font-bold"
							>
								×
							</button>
						</div>

						{/* Offensive Stats Section */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">
								Offensive Performance
							</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="bg-blue-50 p-4 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Total Yards</div>
									<div className="text-2xl font-bold text-blue-600">
										{selectedResults.yards}
									</div>
								</div>
								<div className="bg-green-50 p-4 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Touchdowns</div>
									<div className="text-2xl font-bold text-green-600">
										{selectedResults.touchdowns}
									</div>
								</div>
								<div className="bg-purple-50 p-4 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Yards per Play</div>
									<div className="text-2xl font-bold text-purple-600">
										{selectedResults.yards_per_play}
									</div>
								</div>
								<div className="bg-orange-50 p-4 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Offensive Plays</div>
									<div className="text-2xl font-bold text-orange-600">
										{selectedResults.offensive_plays}
									</div>
								</div>
							</div>
						</div>

						{/* Passing Stats */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">
								Passing
							</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="bg-blue-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Passing Yards</div>
									<div className="text-xl font-bold text-blue-600">
										{selectedResults.passing_yards}
									</div>
								</div>
								<div className="bg-blue-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Pass TDs</div>
									<div className="text-xl font-bold text-blue-600">
										{selectedResults.passing_touchdowns}
									</div>
								</div>
								<div className="bg-blue-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Completions</div>
									<div className="text-xl font-bold text-blue-600">
										{selectedResults.pass_completions}/{selectedResults.pass_attempts}
									</div>
								</div>
								<div className="bg-blue-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Yards per Pass</div>
									<div className="text-xl font-bold text-blue-600">
										{selectedResults.yards_per_pass}
									</div>
								</div>
							</div>
						</div>

						{/* Rushing Stats */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">
								Rushing
							</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="bg-green-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Rushing Yards</div>
									<div className="text-xl font-bold text-green-600">
										{selectedResults.rushing_yards}
									</div>
								</div>
								<div className="bg-green-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Rush TDs</div>
									<div className="text-xl font-bold text-green-600">
										{selectedResults.rushing_touchdowns}
									</div>
								</div>
								<div className="bg-green-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Rush Attempts</div>
									<div className="text-xl font-bold text-green-600">
										{selectedResults.rush_attempts}
									</div>
								</div>
								<div className="bg-green-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Yards per Rush</div>
									<div className="text-xl font-bold text-green-600">
										{selectedResults.yards_per_rush}
									</div>
								</div>
							</div>
						</div>

						{/* Efficiency Stats */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">
								Efficiency
							</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="bg-purple-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">First Downs</div>
									<div className="text-xl font-bold text-purple-600">
										{selectedResults.first_downs}
									</div>
								</div>
								<div className="bg-purple-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">3rd Down Conv.</div>
									<div className="text-xl font-bold text-purple-600">
										{selectedResults.third_down_conversions}/{selectedResults.third_down_attempts}
									</div>
								</div>
								<div className="bg-purple-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Red Zone Conv.</div>
									<div className="text-xl font-bold text-purple-600">
										{selectedResults.red_zone_conversions}/{selectedResults.red_zone_attempts}
									</div>
								</div>
								<div className="bg-purple-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">
										Time of Possession
									</div>
									<div className="text-xl font-bold text-purple-600">
										{selectedResults.time_of_possession}
									</div>
								</div>
							</div>
						</div>

						{/* Turnovers & Sacks */}
						<div className="mb-6">
							<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 border-b border-gray-200 dark:border-dark-border pb-2">
								Turnovers & Protection
							</h3>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
								<div className="bg-red-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Turnovers</div>
									<div className="text-xl font-bold text-red-600">
										{selectedResults.turnovers}
									</div>
								</div>
								<div className="bg-red-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Interceptions</div>
									<div className="text-xl font-bold text-red-600">
										{selectedResults.interceptions_thrown}
									</div>
								</div>
								<div className="bg-red-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Fumbles Lost</div>
									<div className="text-xl font-bold text-red-600">
										{selectedResults.fumbles_lost}
									</div>
								</div>
								<div className="bg-yellow-50 p-3 rounded-lg">
									<div className="text-sm text-gray-600 dark:text-gray-300">Sacks Allowed</div>
									<div className="text-xl font-bold text-yellow-600">
										{selectedResults.sacks_allowed}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default TeamMatchupsTable;
